import {
  ALL_NOTES,
  MODES,
  resolveAllNotesIndex,
  getEnharmonicNoteName,
  getProperNoteDisplay,
  getNotesInMode,
  formatChordQuality
} from './musicTheory';

// Fonctions pures sur le JSON lesson.v1 : validation, sections, identification
// de l'accord d'un pas et transposition. Sans React, sans audio, sans alphaTab.

const LESSON_VERSION = 1;

const cloneStep = (step) => ({
  ...step,
  notes: step.notes.map((note) => ({ ...note }))
});

export const loadLesson = (json) => {
  if (!json || json.v !== LESSON_VERSION) {
    throw new Error('lesson: version non supportee');
  }
  if (!Array.isArray(json.steps)) {
    throw new Error('lesson: steps manquants');
  }
  return {
    ...json,
    sections: Array.isArray(json.sections) ? json.sections.map((section) => ({ ...section })) : [],
    steps: json.steps.map(cloneStep)
  };
};

export const stepsOfSection = (lesson, sectionIndex) => {
  const section = lesson && lesson.sections ? lesson.sections[sectionIndex] : null;
  if (!section) return [];
  return lesson.steps.slice(section.firstStep, section.lastStep + 1);
};

const lowestFret = (step) => Math.min(...step.notes.map((note) => note.fret));

export const isAscendingSection = (steps) => {
  if (!Array.isArray(steps) || steps.length < 2) return false;
  for (let i = 1; i < steps.length; i++) {
    if (!(lowestFret(steps[i]) > lowestFret(steps[i - 1]))) return false;
  }
  return true;
};

// Structures de triade en demi-tons depuis la fondamentale.
const TRIADS = [
  { quality: 'major', symbol: 'maj', intervals: [4, 7] },
  { quality: 'minor', symbol: 'min', intervals: [3, 7] },
  { quality: 'diminished', symbol: 'dim', intervals: [3, 6] },
  { quality: 'augmented', symbol: 'aug', intervals: [4, 8] }
];

const DEGREE_LABELS = { 0: '1', 3: 'b3', 4: '3', 7: '5', 6: 'b5', 8: '#5' };

const pitchClass = (midi) => ((midi % 12) + 12) % 12;

// Cherche la fondamentale et la qualité par la structure des classes de hauteur,
// jamais par la note la plus grave (inversions). L'augmenté est symétrique : sa
// fondamentale est la note la plus grave.
const findTriad = (pcs, bassPc) => {
  for (const triad of TRIADS) {
    const roots = pcs.filter((root) =>
      triad.intervals.every((interval) => pcs.includes((root + interval) % 12))
    );
    if (roots.length === 1) return { root: roots[0], ...triad };
    if (roots.length > 1) return { root: roots.includes(bassPc) ? bassPc : roots[0], ...triad };
  }
  return null;
};

const spellRoot = (name, context) => {
  if (!context || !context.rootNote) return name;
  const { rootNote, mode } = context;
  const scaleNotes = mode && MODES[mode] ? getNotesInMode(rootNote, mode) : [];
  if (scaleNotes.length) {
    return getProperNoteDisplay(name, { notes: scaleNotes, scaleRoot: rootNote, modeName: mode });
  }
  return getEnharmonicNoteName(name, rootNote, mode);
};

const degreesFromRoot = (notes, rootPc) => {
  const degrees = {};
  notes.forEach((note) => {
    const label = DEGREE_LABELS[(pitchClass(note.midi) - rootPc + 12) % 12];
    if (label) degrees[`${note.string}:${note.fret}`] = label;
  });
  return degrees;
};

export const identifyStepChord = (step, context = {}) => {
  const notes = step && Array.isArray(step.notes) ? step.notes : [];
  if (!notes.length) {
    return { root: null, quality: 'unknown', name: '', degrees: {} };
  }
  const bass = notes.reduce((lowest, note) => (note.midi < lowest.midi ? note : lowest), notes[0]);
  const bassPc = pitchClass(bass.midi);
  const pcs = [...new Set(notes.map((note) => pitchClass(note.midi)))];

  if (pcs.length === 1) {
    const root = spellRoot(ALL_NOTES[bassPc], context);
    return { root, quality: 'note', name: root, degrees: degreesFromRoot(notes, bassPc) };
  }

  const triad = pcs.length === 3 ? findTriad(pcs, bassPc) : null;
  if (!triad) {
    const root = spellRoot(ALL_NOTES[bassPc], context);
    return { root, quality: 'unknown', name: '', degrees: degreesFromRoot(notes, bassPc) };
  }

  const root = spellRoot(ALL_NOTES[triad.root], context);
  return {
    root,
    quality: triad.quality,
    name: `${root}${formatChordQuality(triad.symbol)}`,
    degrees: degreesFromRoot(notes, triad.root)
  };
};

export const shortestTransposition = (fromRoot, toRoot) => {
  const from = resolveAllNotesIndex(fromRoot);
  const to = resolveAllNotesIndex(toRoot);
  if (from === -1 || to === -1) {
    throw new Error('lesson: note inconnue');
  }
  const d = ((to - from) % 12 + 12) % 12;
  return d > 5 ? d - 12 : d;
};

const shiftStep = (step, semitones, fretCount) => {
  let notes = step.notes.map((note) => ({
    ...note,
    fret: note.fret + semitones,
    midi: note.midi + semitones
  }));
  const shiftOctave = (delta) => {
    notes = notes.map((note) => ({ ...note, fret: note.fret + delta, midi: note.midi + delta }));
  };
  if (notes.some((note) => note.fret < 0)) shiftOctave(12);
  if (notes.some((note) => note.fret > fretCount)) shiftOctave(-12);
  return { ...step, notes };
};

export const transposeSteps = (steps, semitones, { fretCount = 22 } = {}) => {
  const shifted = steps.map((step) => shiftStep(step, semitones, fretCount));

  const sectionOrder = [];
  const bySection = new Map();
  steps.forEach((step, i) => {
    if (!bySection.has(step.sectionIndex)) {
      bySection.set(step.sectionIndex, []);
      sectionOrder.push(step.sectionIndex);
    }
    bySection.get(step.sectionIndex).push(i);
  });

  const result = shifted.slice();
  sectionOrder.forEach((sectionIndex) => {
    const indexes = bySection.get(sectionIndex);
    if (!isAscendingSection(indexes.map((i) => steps[i]))) return;
    const reordered = indexes
      .map((i) => shifted[i])
      .sort((a, b) => lowestFret(a) - lowestFret(b));
    indexes.forEach((i, k) => {
      result[i] = { ...reordered[k], time: steps[i].time };
    });
  });
  return result;
};
